import Logger from 'config/logging';
import _ from 'lodash';
import moment from 'moment';
import { perfCount } from 'libs/perf/rosterRenderPerf';

// **********************************
// Helper functions
// **********************************
export const PIVOT_TYPES = {
  USER: 'USER',
  ACTIVITY: 'ACTIVITY'
};

const isUser = pivot => (pivot === PIVOT_TYPES.USER);
const isActivity = pivot => (pivot === PIVOT_TYPES.ACTIVITY);

/**
 * Given the timeline options, returns the associated PIVOT_TYPE
 * @param {any} timelineOptions
 * @returns {object} PIVOT_TYPE
 */
export const getGroup = (timelineOptions) => {
  if (timelineOptions.rosterPivoted) {
    return PIVOT_TYPES.ACTIVITY;
  }
  return PIVOT_TYPES.USER;
};

export const getRosterGroups = (pivotType, userGroups, activityGroups) => (isActivity(pivotType) ? activityGroups : userGroups);

export function getGroupTitle(pivotType, props, extraUsers = false) {
  if (isUser(pivotType)) {
    const { user_groups } = props;
    return `${user_groups.length - 1}${extraUsers ? '+' : ''} User${user_groups.length === 2 ? '' : 's'}`;
  }
  if (isActivity(pivotType)) {
    const { activity_groups } = props;
    return `${activity_groups.length} Activit${activity_groups.length === 1 ? 'y' : 'ies'}`;
  }
  return '';
}

export const getActivityGroupItems = (activity_groups, localItems, serverItems, deletedItems) => {
  const union = _.unionBy(localItems, serverItems, 'id');
  const filtered = _.filter(union, item => !_.includes(deletedItems, item.id));

  for (let i = 0; i < filtered.length; i++) {
    const item = filtered[i];

    if (!moment.isMoment(item.start_time)) {
      item.start_time = moment(item.start_time);
    }
    if (!moment.isMoment(item.end_time)) {
      item.end_time = moment(item.end_time);
    }

    const group = _.find(activity_groups, groupItem => groupItem.aid === item.activity.id);
    item.group = group ? group.id : null;
  }

  // filter items not in visible groups (due to filtering)
  return filtered.filter(item => item.group !== null);
};

export const getUserGroupItems = (userGroups, localItems, serverItems, deletedItems, travelItems = [], leaveItems = [], validationTasks = []) => {
  const union = _.unionBy(localItems, serverItems, travelItems, leaveItems, 'id');

  // Index groups by uid and validations by assignment id once, so the per-item work is
  // O(1) lookups instead of a linear _.find over groups and a full validationTasks scan.
  // Overall O(A+U+V) rather than O(A*(U+V)).
  const groupByUid = new Map(userGroups.map(g => [g.uid, g]));
  const validationsById = new Map();
  for (let i = 0; i < validationTasks.length; i++) {
    const task = validationTasks[i];
    const existing = validationsById.get(task.id);
    if (existing) {
      existing.push(task);
    }
    else {
      validationsById.set(task.id, [task]);
    }
  }

  const results = [];
  for (let i = 0; i < union.length; i++) {
    const item = union[i];
    if (_.includes(deletedItems, item.id)) {
      continue; // no deleted
    }

    // Resolve the visible group. Items with a numeric user_id map to a user group
    // (its id, or null when the user isn't visible); other items (travel/leave without a
    // numeric user_id) keep whatever group they already had.
    let { group } = item;
    if (typeof item.user_id === 'number') {
      const matched = groupByUid.get(item.user_id);
      group = matched ? matched.id : null;
    }
    if (group === null) {
      continue; // filter items not in visible groups (due to filtering)
    }

    // Build a NEW item (never mutate the reducer-owned source) with Moment times and its
    // validations attached.
    results.push({
      ...item,
      start_time: moment.isMoment(item.start_time) ? item.start_time : moment(item.start_time),
      end_time: moment.isMoment(item.end_time) ? item.end_time : moment(item.end_time),
      group,
      validations: validationsById.get(item.id) || []
    });
  }

  return results;
};

// @param groups will be either user or activity groups - depending on pivot
export function getRosterItems(pivotType, groups, localItems, serverItems, deletedItems, travelItems = [], leaveItems = [], validationTasks = []) {
  perfCount('deriveRosterItems');
  if (pivotType === PIVOT_TYPES.USER) {
    return getUserGroupItems(groups, localItems, serverItems, deletedItems, travelItems, leaveItems, validationTasks);
  }
  if (pivotType === PIVOT_TYPES.ACTIVITY) {
    return getActivityGroupItems(groups, localItems, serverItems, deletedItems);
  }
  Logger.error(`Error - unknown pivot mode - ${pivotType}`);
  return [];
}

// Only user pivot has layers atm
export const getLayers = (pivotType, layers = []) => {
  if (isUser(pivotType)) {
    return layers;
  }
  return [];
};
